import type { TrelloWorkspace } from './trelloTypes';

function getSelectedOrganizationId(workspaces: TrelloWorkspace[]) {
  return workspaces[0]?.atlassianOrganizationId ?? null;
}

function keepSameOrganizationAsFirst(workspaces: TrelloWorkspace[]) {
  const selectedOrganizationId = getSelectedOrganizationId(workspaces);
  return workspaces.filter(
    (workspace) =>
      (workspace.atlassianOrganizationId ?? null) === selectedOrganizationId,
  );
}

export function normalizeTrelloWorkspaceSelection(
  workspaces: TrelloWorkspace[],
  isMultiSelectEnabled: boolean,
): TrelloWorkspace[] {
  if (workspaces.length <= 1) {
    return workspaces;
  }

  return isMultiSelectEnabled
    ? keepSameOrganizationAsFirst(workspaces)
    : workspaces.slice(0, 1);
}

/**
 * Pick the workspace(s) to pre-select when the selector first loads.
 *
 * - No workspaces  → empty selection (nothing to prefill).
 * - One workspace  → auto-select it.
 * - Many workspaces → empty selection.
 */
export function getInitialTrelloWorkspaceSelection(
  workspaces: TrelloWorkspace[],
): TrelloWorkspace[] {
  if (workspaces.length === 1) {
    return [workspaces[0]];
  }
  return [];
}

/**
 * Whether a workspace can still be added to the current selection.
 *
 * Workspaces combine only when they share an organization, or when none of
 * them belong to one. Ineligible workspaces stay in the list and are rendered
 * disabled, so the set of options never changes shape as the user selects.
 */
export function isTrelloWorkspaceSelectable({
  workspace,
  selectedWorkspaces,
  isMultiSelectEnabled,
}: {
  workspace: TrelloWorkspace;
  selectedWorkspaces: readonly TrelloWorkspace[];
  isMultiSelectEnabled: boolean;
}): boolean {
  if (!isMultiSelectEnabled || selectedWorkspaces.length === 0) {
    return true;
  }

  return (
    (workspace.atlassianOrganizationId ?? null) ===
    getSelectedOrganizationId([...selectedWorkspaces])
  );
}

/** True when any workspace belongs to an Atlassian organization. */
export function hasEnterpriseTrelloWorkspace(
  workspaces: readonly TrelloWorkspace[],
): boolean {
  return workspaces.some(
    (workspace) => workspace.atlassianOrganizationId != null,
  );
}

/**
 * Compares workspaces by display name.
 *
 * Exposed so callers holding a wrapper around a workspace can sort in place
 * rather than sorting the workspaces and mapping back to their wrappers.
 */
export function compareTrelloWorkspacesByName(
  a: TrelloWorkspace,
  b: TrelloWorkspace,
): number {
  return a.displayName.localeCompare(b.displayName, undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * Orders workspaces by display name.
 *
 * Sorting is applied to the accumulated list rather than to each page, so the
 * ordering holds across pagination: the connection has no sort argument, and
 * later pages would otherwise append out of sequence.
 */
export function sortTrelloWorkspacesByName<T extends TrelloWorkspace>(
  workspaces: readonly T[],
): T[] {
  return [...workspaces].sort(compareTrelloWorkspacesByName);
}

/**
 * Compares workspaces by Enterprise ownership first, then by display name.
 *
 * Enterprise-owned workspaces lead the list, each block ordered alphabetically
 * within itself.
 */
export function compareTrelloWorkspacesByEnterpriseThenName(
  a: TrelloWorkspace,
  b: TrelloWorkspace,
): number {
  const aIsEnterprise = a.atlassianOrganizationId != null;
  const bIsEnterprise = b.atlassianOrganizationId != null;

  if (aIsEnterprise !== bIsEnterprise) {
    return aIsEnterprise ? -1 : 1;
  }

  return compareTrelloWorkspacesByName(a, b);
}
